import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Divider
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../../request/http-common';

const AdminJointAccountsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');

  const { data: accountsData, isLoading, error, refetch } = useQuery(
    ['adminJointAccounts', searchTerm, statusFilter],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await apiClient.get(`/api/admin/joint-accounts?${params}`);
      return response.data;
    }
  );

  const { data: dashboardData } = useQuery(
    'adminJointAccountsDashboard',
    async () => {
      const response = await apiClient.get('/api/admin/joint-accounts/dashboard');
      return response.data;
    }
  );

  const { mutate: updateAccountStatus, isLoading: isUpdating } = useMutation(
    async ({ accountId, status, reason }) => {
      const response = await apiClient.put(`/api/admin/joint-accounts/${accountId}/status`, {
        status,
        reason
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setActionDialogOpen(false);
        setSelectedAccount(null);
        setActionReason('');
        refetch();
      },
      onError: (error) => {
        console.error('Failed to update account status:', error);
      }
    }
  );

  const { mutate: approvePendingAccount, isLoading: isApproving } = useMutation(
    async ({ accountId, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/joint-accounts/${accountId}/approve`, {
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        setActionDialogOpen(false);
        setSelectedAccount(null);
        setActionReason('');
        refetch();
      },
      onError: (error) => {
        console.error('Failed to approve pending account:', error);
      }
    }
  );

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'warning';
      case 'closed': return 'error';
      default: return 'default';
    }
  };

  const handleStatusChange = (account, newStatus) => {
    setSelectedAccount(account);
    setActionType(newStatus);
    setActionDialogOpen(true);
  };

  const handleApprovePending = (account) => {
    setSelectedAccount(account);
    setActionType('approve');
    setActionDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedAccount || !actionType || !actionReason.trim()) {
      return;
    }

    if (actionType === 'approve') {
      approvePendingAccount({
        accountId: selectedAccount.id,
        adminNotes: actionReason
      });
    } else {
      updateAccountStatus({
        accountId: selectedAccount.id,
        status: actionType,
        reason: actionReason
      });
    }
  };

  const accounts = accountsData?.joint_accounts?.data || [];
  const stats = dashboardData?.statistics || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 3 }}>
        Joint Accounts Management
      </Typography>

      {/* Dashboard Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: '#F7931A', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {stats.total_joint_accounts || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 48, color: '#10B981', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#10B981', fontWeight: 700, mb: 1 }}>
                {formatPrice(stats.total_balance)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Total Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 48, color: '#3B82F6', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {stats.total_members || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Active Members
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#FFB800', mb: 2 }} />
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                {stats.pending_transactions || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6C7293' }}>
                Pending Transactions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139',
        mb: 3
      }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#6C7293', mr: 1 }} />,
                  style: { color: '#FFFFFF' }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#2B3139' },
                    '&:hover fieldset': { borderColor: '#F7931A' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#6C7293' }}>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ color: '#FFFFFF' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/joint-accounts/transactions')}
                sx={{
                  background: 'linear-gradient(135deg, #F7931A 0%, #FFB800 100%)',
                  width: '100%'
                }}
              >
                Manage Transactions
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/joint-accounts/approvals')}
                sx={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  width: '100%'
                }}
              >
                Pending Approvals
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Accounts Table */}
      <Card sx={{
        background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
        border: '1px solid #2B3139'
      }}>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Failed to load joint accounts
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Account</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Owner</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Balance</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Members</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id} sx={{ '&:hover': { backgroundColor: 'rgba(247, 147, 26, 0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#F7931A', width: 40, height: 40 }}>
                            <AccountBalanceIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#FFFFFF' }}>
                              {account.account_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6C7293' }}>
                              #{account.account_number}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {account.creator?.name || account.creator?.email}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6C7293' }}>
                          {account.creator?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ color: '#10B981', fontWeight: 600 }}>
                          {formatPrice(account.balance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                          {account.active_members_count || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.status}
                          color={getStatusColor(account.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6C7293' }}>
                          {new Date(account.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            onClick={() => navigate(`/admin/joint-accounts/${account.id}`)}
                            sx={{ color: '#3B82F6' }}
                            size="small"
                          >
                            <VisibilityIcon />
                          </IconButton>
                          
                          {/* Pending Account - Show Activate Button */}
                          {account.status === 'pending' && (
                            <IconButton
                              onClick={() => handleApprovePending(account)}
                              sx={{ color: '#10B981' }}
                              size="small"
                              title="Activate Account"
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          )}
                          
                          {/* Active Account - Show Suspend Button */}
                          {account.status === 'active' && (
                            <IconButton
                              onClick={() => handleStatusChange(account, 'suspended')}
                              sx={{ color: '#FFB800' }}
                              size="small"
                            >
                              <BlockIcon />
                            </IconButton>
                          )}
                          
                          {/* Suspended Account - Show Activate Button */}
                          {account.status === 'suspended' && (
                            <IconButton
                              onClick={() => handleStatusChange(account, 'active')}
                              sx={{ color: '#10B981' }}
                              size="small"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          )}
                          
                          {/* All accounts except closed - Show Close Button */}
                          {account.status !== 'closed' && (
                            <IconButton
                              onClick={() => handleStatusChange(account, 'closed')}
                              sx={{ color: '#F84960' }}
                              size="small"
                            >
                              <CancelIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
            border: '1px solid #2B3139',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', borderBottom: '1px solid #2B3139' }}>
          {actionType === 'approve' ? 'Activate Pending Account' : 'Confirm Account Action'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
            {actionType === 'approve' 
              ? `Are you sure you want to activate the pending account "${selectedAccount?.account_name}"?`
              : `Are you sure you want to ${actionType} the account "${selectedAccount?.account_name}"?`
            }
          </Typography>
          
          <TextField
            fullWidth
            label={actionType === 'approve' ? "Admin notes (optional)" : "Reason for action"}
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            multiline
            rows={3}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': { color: '#6C7293' },
              '& .MuiOutlinedInput-root': {
                color: '#FFFFFF',
                '& fieldset': { borderColor: '#2B3139' },
                '&:hover fieldset': { borderColor: '#F7931A' }
              }
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setActionDialogOpen(false)}
              sx={{ color: '#6C7293' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={(!actionReason.trim() && actionType !== 'approve') || isUpdating || isApproving}
              variant="contained"
              sx={{
                background: actionType === 'approve' 
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #F84960 0%, #DC2626 100%)',
                '&:disabled': { background: '#2B3139' }
              }}
            >
              {isUpdating || isApproving ? <CircularProgress size={20} /> : (actionType === 'approve' ? 'Activate' : 'Confirm')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminJointAccountsList; 